/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file getArguments internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\get-arguments
 */

import { ComponentType } from "react";
import { IAsyncFunction } from "../";
import { IAsyncPlaceholderProps, IOptions } from "../../";

/**
 * Unpack and return the arguments supplied to use-async (along with defaults)
 * @param placeHolderOrOptions Either a placeholder, or the options object
 * @param options If it exists, it means the first argument was a placeholder, and this is the options object.
 * @return All the options, defaulted if necessary, along with a Placeholder
 */
export function getArguments<TAsync extends IAsyncFunction, TPlaceholderProps extends IAsyncPlaceholderProps>(
	placeHolderOrOptions?: ComponentType<TPlaceholderProps> | IOptions<TAsync>,
	options?: IOptions<TAsync>,
): IOptions<TAsync> & { executeImmediately: boolean; Placeholder?: ComponentType<TPlaceholderProps> } {
	let Placeholder: ComponentType<TPlaceholderProps> | undefined = undefined;

	if (placeHolderOrOptions) {
		const p = placeHolderOrOptions as any;
		if (typeof p === "function" || (p.$$typeof && typeof p.type === "function")) {
			Placeholder = placeHolderOrOptions as ComponentType<TPlaceholderProps>;
		} else {
			options = placeHolderOrOptions as IOptions<TAsync>;
		}
	}

	// Default option values
	options = options || {
		executeImmediately: false,
		initialArguments: undefined,
		onFailure: undefined,
		onSuccess: undefined,
		displayName: undefined,
	};

	return { Placeholder, ...options, executeImmediately: !!options.executeImmediately };
}
